import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SortingDirection } from 'src/graphql/generated/graphql';
import { queryStringToObject } from 'src/utils/query-string';

interface UseTableProps<FilterT, SortingT> {
  defaultFilter?: FilterT;
  defaultSorting?: SortingT;
}

export function useTable<FilterT, SortingT>({
  defaultSorting,
  defaultFilter,
}: UseTableProps<FilterT, SortingT> = {}) {
  const [searchParams] = useSearchParams();
  const defaultFilterFromUrl = queryStringToObject(
    searchParams.toString(),
  )?.filter;

  const initialFilter = defaultFilterFromUrl || defaultFilter;
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<FilterT>(initialFilter);
  const [sorting, setSorting] = useState<SortingT | undefined>(defaultSorting);
  const [sortingField, sortingDirection] = sorting
    ? Object.entries(sorting)?.[0]
    : [];

  const onFilterChange = useCallback((filter: FilterT) => {
    setPage(0);
    setFilter(filter);
  }, []);

  const onSortChange = (
    changedColumn: keyof SortingT,
    direction: 'desc' | 'asc',
  ) => {
    const newSorting = {
      [changedColumn]:
        direction === 'desc' ? SortingDirection.Desc : SortingDirection.Asc,
    };
    setSorting(newSorting as SortingT);
  };

  return {
    onFilterChange,
    onSortChange,
    page,
    setPage,
    filter,
    sorting,
    sortingField,
    sortingDirection,
    defaultFilter: initialFilter,
    setFilter,
  };
}
